/**
 * Status chip for a patient's EHR link, in the squared style of
 * ResourceStateChip. Unlinked is outlined rather than tinted, so an ended link
 * reads differently from an active grey one.
 */
import { Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import React from 'react';
import { useIntl } from 'react-intl';

import type { EhrPatientLinkStatus } from '@models/ehr-sync';

import { linkStatusMessages } from '../messages';
import type { EhrSyncTone } from './tone';
import { toneTextColor, toneTintColor } from './tone';

/** Props for {@link PatientLinkStatusChip}. */
interface PatientLinkStatusChipProps {
  status: EhrPatientLinkStatus;
}

/** Tone per status, as signed off in the VEG-784 design. */
export const LINK_STATUS_TONE: Record<EhrPatientLinkStatus, EhrSyncTone> = {
  pending: 'grey',
  needsReview: 'warning',
  autoLinked: 'teal',
  linked: 'success',
  rejected: 'error',
  noMatch: 'orange',
  paused: 'grey',
  unlinked: 'grey',
};

/**
 * Squared chip with the localized link status.
 *
 * @param props - see {@link PatientLinkStatusChipProps}
 */
export default function PatientLinkStatusChip({
  status,
}: PatientLinkStatusChipProps) {
  const { formatMessage } = useIntl();
  const tone = LINK_STATUS_TONE[status];
  const outlined = status === 'unlinked';

  return (
    <Typography
      component="span"
      variant="caption"
      fontWeight="bold"
      data-tone={tone}
      data-outlined={outlined || undefined}
      sx={(theme) => ({
        display: 'inline-flex',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        color: toneTextColor(theme, tone),
        backgroundColor: outlined
          ? 'transparent'
          : alpha(toneTintColor(theme, tone), 0.14),
        border: 1,
        borderColor: outlined ? 'divider' : 'transparent',
        borderRadius: 0.75,
        px: 1,
        py: 0.375,
      })}
    >
      {formatMessage(linkStatusMessages[status])}
    </Typography>
  );
}
